import { type DatabaseChangeType } from '@databases/types/redux';
import {
  addChangeToChangeset,
  type DatabaseChange,
  type DatabaseChangeset,
} from '@databases/utils/database/changesets';
import { createSlice, type PayloadAction } from '@databases/utils/shims/redux-toolkit';

export interface ChangesetsState {
  scopes: Record<
    string,
    {
      /**
       * Drafted changeset by content ID. There can be multiple simultaneous drafts for the same database
       * (for example when editing an entry in the entry dialog), hence the scope.
       */
      draftedChangesets: Record<string, DatabaseChangeset>;

      /**
       * The currently saving changeset by content ID if anything is saving right now. When saving a draft, the
       * changeset is removed from draftedChangesets and moved to savingChangeset while it is saving. This way
       * the user can continue making changes (which will be added to draftedChangesets) while a draft is saving.
       */
      savingChangeset: Record<string, DatabaseChangeset>;

      /**
       * The number of running save operations by content ID within this scope.
       */
      activeSaveOperations: Record<string, number>;
    }
  >;

  /**
   * The total number of running save operations by content ID, across all scopes. This includes save operations
   * that are run outside of a scope, such as locking/unlocking the database.
   */
  activeSaveOperations: Record<string, number>;
}

export function ensureScope(state: ChangesetsState, scope: string): void {
  if (!state.scopes[scope]) {
    state.scopes[scope] = {
      draftedChangesets: {},
      savingChangeset: {},
      activeSaveOperations: {},
    };
  }
}

export const initialChangesetsState: ChangesetsState = {
  scopes: {},
  activeSaveOperations: {},
};

const changesetsSlice = createSlice({
  name: 'databaseUpdates',
  initialState: initialChangesetsState,
  reducers: {
    addChangesToDraft: (
      state,
      action: PayloadAction<{
        contentId: string;
        scope: string;
        changes: DatabaseChange[];
        before?: boolean;
      }>
    ) => {
      const { contentId, scope, changes, before } = action.payload;

      ensureScope(state, scope);

      if (before) {
        state.scopes[scope].draftedChangesets[contentId] = addChangeToChangeset(
          [],
          ...changes,
          ...state.scopes[scope].draftedChangesets[contentId]
        );
      } else {
        state.scopes[scope].draftedChangesets[contentId] = addChangeToChangeset(
          state.scopes[scope].draftedChangesets[contentId] || [],
          ...changes
        );
      }
    },

    removeChangesFromDraft: (
      state,
      action: PayloadAction<{
        contentId: string;
        scope: string;
        changes: DatabaseChangeType[];
      }>
    ) => {
      const { contentId, scope, changes } = action.payload;

      ensureScope(state, scope);

      state.scopes[scope].draftedChangesets[contentId] = state.scopes[
        scope
      ].draftedChangesets[contentId].filter((change) => !changes.includes(change.type));
    },

    setDraftedChangeset: (
      state,
      action: PayloadAction<{
        contentId: string;
        scope: string;
        changeset: DatabaseChangeset;
      }>
    ) => {
      const { contentId, scope, changeset } = action.payload;

      ensureScope(state, scope);

      state.scopes[scope].draftedChangesets[contentId] = changeset;
    },

    setSavingChangeset: (
      state,
      action: PayloadAction<{
        contentId: string;
        scope: string;
        changeset: DatabaseChangeset;
      }>
    ) => {
      const { contentId, scope, changeset } = action.payload;

      ensureScope(state, scope);

      state.scopes[scope].savingChangeset[contentId] = changeset;
    },

    startSaveOperation: (
      state,
      action: PayloadAction<{ contentId: string; scope?: string }>
    ) => {
      const { contentId, scope } = action.payload;

      state.activeSaveOperations[contentId] =
        (state.activeSaveOperations[contentId] ?? 0) + 1;
      if (scope) {
        ensureScope(state, scope);
        state.scopes[scope].activeSaveOperations[contentId] =
          (state.scopes[scope].activeSaveOperations[contentId] ?? 0) + 1;
      }
    },

    endSaveOperation: (
      state,
      action: PayloadAction<{ contentId: string; scope?: string }>
    ) => {
      const { contentId, scope } = action.payload;

      state.activeSaveOperations[contentId] =
        (state.activeSaveOperations[contentId] ?? 0) - 1;
      if (scope) {
        ensureScope(state, scope);
        state.scopes[scope].activeSaveOperations[contentId] =
          (state.scopes[scope].activeSaveOperations[contentId] ?? 0) - 1;
      }
    },
  },
});

export default changesetsSlice;
